import { useState, useEffect } from "react";
import React from "react";
import { getFirestore, getDoc, doc, updateDoc } from "firebase/firestore";
import app from "./firebase";
import { useCookies } from "react-cookie";
import emailjs from "emailjs-com";
import { Helmet } from "react-helmet";
import { Leaf, Eye, EyeOff, ArrowRight, Mail, Lock, AlertCircle, CheckCircle2, Sprout, TrendingUp, ShieldCheck } from "lucide-react";

const db = getFirestore(app);

export default function Home() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [errorPanel, setErrorPanel] = useState(false);
  const [successPanel, setSuccessPanel] = useState(false);
  const [f2a, show2FAField] = useState(false);
  const [message, setMessage] = useState("Incorrect details");
  const [cookies, setCookie] = useCookies(["username", "name"]);
  const [users, setUsers] = useState([]);
  const [otp, setOTP] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('timeout') || urlParams.get('expired')) {
      showError("Session has expired. Please log in again.");
    }
  }, []);

  const forgotPassword = () => { window.location.href = "/forgotpassword"; };

  const showError = (msg) => {
    setMessage(msg);
    setErrorPanel(true);
    setSuccessPanel(false);
    setTimeout(() => setErrorPanel(false), 5000);
  };

  const sendSigninNotification = async (uId, uEmail) => {
    try {
      const notifRef = doc(db, "notification", uId);
      const notifSnap = await getDoc(notifRef);
      if (notifSnap.exists() && notifSnap.data().signin) {
        const templateParams = {
          header: "AGRIVESTIA SECURITY",
          subject: "NEW SIGN-IN DETECTED",
          message: `Hi, a new sign-in was detected on your Agrivestia account (${uEmail}) at ${new Date().toLocaleString()}. If this wasn't you, please change your password immediately.`,
          email: uEmail
        };
        await emailjs.send("service_pehhhv8", "template_rql77hn", templateParams, "9o1vflADP-wePeEFd");
      }
    } catch (err) { console.error("Notification Error:", err); }
  };

  const login = async () => {
    if (!username) { showError("Please enter your email address"); return; }
    if (!password) { showError("Please enter your password"); return; }
    setLoading(true);
    try {
      const userId = username.replace("@", "").replace(".", "").toLowerCase();
      const docRef = doc(db, "users", userId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const userData = { id: docSnap.id, ...docSnap.data() };
        setUsers(userData);
        if (userData.status === "active") {
          if (userData.accountStatus === "disabled") {
            showError("This account has been disabled by the administrator. Please contact support.");
            return;
          }
          if (userData.password === password) {
            if (!userData.emailVerified) {
              window.location.href = `/verify_email?email=${encodeURIComponent(username)}`;
              return;
            }
            if (!userData.factor2) {
              setErrorPanel(false);
              setSuccessPanel(true);
              sessionStorage.setItem("sessionActive", "1");
              setCookie("username", userId, { path: '/' });
              setCookie("name", userData.name || "User", { path: '/' });
              sendSigninNotification(userId, username);
              await updateDoc(docRef, { lastLoginDate: new Date().toISOString() });
              setTimeout(() => { window.location.href = "/market_dashboard"; }, 800);
            } else {
              show2FAField(true);
              setErrorPanel(false);
              const randomstring = Math.floor(Math.random() * 9999) + 1000;
              setOTP(randomstring);
              const templateParams = { header: "AGRIVESTIA", subject: "AGRIVESTIA LOGIN AUTHENTICATION", message: "Hi, here is your OTP from AGRIVESTIA: " + randomstring, email: username };
              emailjs.send("service_pehhhv8", "template_rql77hn", templateParams, "9o1vflADP-wePeEFd").catch(err => console.error("FAILED...", err));
            }
          } else {
            showError("Incorrect password. Please try again.");
          }
        } else {
          showError("This account has been deactivated. Please contact support.");
        }
      } else {
        showError("No account found with this email address.");
      }
    } catch (error) {
      console.error("Error fetching users: ", error);
      showError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const changeOTP = async (event) => {
    const userId = username.replace("@", "").replace(".", "").toLowerCase();
    if (event.target.value == otp) {
      if (!users.emailVerified) {
        window.location.href = `/verify_email?email=${encodeURIComponent(username)}`;
        return;
      }
      setErrorPanel(false);
      setSuccessPanel(true);
      sessionStorage.setItem("sessionActive", "1");
      setCookie("username", userId, { path: '/' });
      setCookie("name", users.name || "User", { path: '/' });
      sendSigninNotification(userId, username);
      try {
        await updateDoc(doc(db, "users", userId), { lastLoginDate: new Date().toISOString() });
      } catch (err) { console.error(err); }
      setTimeout(() => { window.location.href = "/market_dashboard"; }, 600);
    }
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") login(); };

  const perks = [
    { icon: <TrendingUp size={18} />, text: "Real-time investment tracking" },
    { icon: <ShieldCheck size={18} />, text: "Bank-level security" },
    { icon: <Sprout size={18} />, text: "Access to 12+ crop categories" },
  ];

  return (
    <div style={{ minHeight: "100vh", display: "flex", fontFamily: "var(--font-sans)" }}>
      <Helmet>
        <title>Login — Agrivestia | Invest in Agriculture</title>
        <meta name="description" content="Login to your Agrivestia account and manage your farm investments." />
      </Helmet>

      {/* ── Left brand panel ── */}
      <div className="hide-mobile" style={{
        width: "48%", background: "var(--gradient-hero)",
        display: "flex", flexDirection: "column", justifyContent: "center",
        padding: "60px", position: "relative", overflow: "hidden",
      }}>
        {/* Background dots */}
        <div style={{ position: "absolute", inset: 0, opacity: 0.05, backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div style={{ position: "absolute", bottom: "-80px", right: "-80px", width: "400px", height: "400px", background: "radial-gradient(circle, rgba(34,197,94,0.2) 0%, transparent 70%)" }} />

        <div style={{ position: "relative" }}>
          {/* Logo */}
          <div onClick={() => window.location.href = '/'} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "60px", cursor: "pointer" }}>
            <div style={{ width: "40px", height: "40px", borderRadius: "12px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Leaf size={20} color="white" />
            </div>
            <span style={{ fontWeight: 800, fontSize: "22px", color: "white", letterSpacing: "-0.02em" }}>AGRIVESTIA</span>
          </div>

          <h1 style={{ fontSize: "clamp(28px, 3vw, 42px)", fontWeight: 900, color: "white", lineHeight: 1.2, marginBottom: "20px", letterSpacing: "-0.02em" }}>
            Welcome back,{" "}
            <span style={{ background: "linear-gradient(135deg, #22c55e, #86efac)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Investor
            </span>
          </h1>
          <p style={{ fontSize: "16px", color: "rgba(255,255,255,0.7)", lineHeight: 1.7, marginBottom: "40px", maxWidth: "360px" }}>
            Sign in to your dashboard and continue growing your agricultural portfolio with confidence.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {perks.map((p, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.3)", display: "flex", alignItems: "center", justifyContent: "center", color: "#22c55e", flexShrink: 0 }}>
                  {p.icon}
                </div>
                <span style={{ fontSize: "14px", color: "rgba(255,255,255,0.8)", fontWeight: 500 }}>{p.text}</span>
              </div>
            ))}
          </div>

          <div style={{ marginTop: "32px" }}>
            <button
              onClick={() => window.location.href = "/blog"}
              style={{
                background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
                color: "white", padding: "10px 20px", borderRadius: "10px", fontSize: "14px",
                fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: "8px"
              }}>
              <TrendingUp size={16} color="#22c55e" /> Visit our Blog
            </button>
          </div>

          {/* Social proof */}
          <div style={{ marginTop: "48px", padding: "20px", background: "rgba(255,255,255,0.06)", borderRadius: "var(--radius-lg)", border: "1px solid rgba(255,255,255,0.1)" }}>
            <div style={{ display: "flex", gap: "24px" }}>
              {[["500+", "Investors"], ["₦2B+", "Traded"], ["98%", "Uptime"]].map(([v, l]) => (
                <div key={l} style={{ textAlign: "center", flex: 1 }}>
                  <div style={{ fontSize: "22px", fontWeight: 800, color: "#22c55e" }}>{v}</div>
                  <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.5)", marginTop: "2px" }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        justifyContent: "center", alignItems: "center",
        padding: "clamp(20px, 5vh, 40px) clamp(24px, 5vw, 80px)",
        background: "var(--color-gray-50)",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column"
      }}>
        {/* Mobile logo */}
        <div className="show-mobile" onClick={() => window.location.href = '/'} style={{ display: "none", alignItems: "center", gap: "8px", marginBottom: "32px", cursor: "pointer" }}>
          <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Leaf size={16} color="white" />
          </div>
          <span style={{ fontWeight: 800, fontSize: "18px", color: "var(--color-primary-dark)" }}>AGRIVESTIA</span>
        </div>

        <div style={{ width: "100%", maxWidth: "420px" }}>
          {/* Header */}
          <div style={{ marginBottom: "36px" }}>
            <h2 style={{ fontSize: "28px", fontWeight: 800, color: "var(--color-gray-900)", marginBottom: "8px", letterSpacing: "-0.02em" }}>Sign In</h2>
            <p style={{ fontSize: "15px", color: "var(--color-gray-500)" }}>Enter your credentials to access your dashboard</p>
          </div>

          {/* Error toast */}
          {errorPanel && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", padding: "14px 16px", background: "var(--color-danger-light)", borderRadius: "var(--radius-md)", border: "1px solid #fca5a5", marginBottom: "24px", animation: "fadeInUp 0.3s ease" }}>
              <AlertCircle size={18} color="#dc2626" style={{ flexShrink: 0, marginTop: "1px" }} />
              <p style={{ fontSize: "14px", color: "#991b1b", margin: 0, lineHeight: 1.5 }}>{message}</p>
            </div>
          )}

          {/* Success */}
          {successPanel && (
            <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "14px 16px", background: "var(--color-success-light)", borderRadius: "var(--radius-md)", border: "1px solid #86efac", marginBottom: "24px", animation: "fadeInUp 0.3s ease" }}>
              <CheckCircle2 size={18} color="#16a34a" />
              <p style={{ fontSize: "14px", color: "#15803d", margin: 0, fontWeight: 500 }}>Login successful! Redirecting…</p>
            </div>
          )}

          {/* Form */}
          <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
            {/* Email */}
            <div className="input-group">
              <label className="input-label">Email Address</label>
              <div style={{ position: "relative" }}>
                <Mail size={16} color="var(--color-gray-400)" style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)" }} />
                <input
                  type="text"
                  className="input-field"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="you@example.com"
                  style={{ paddingLeft: "42px" }}
                />
              </div>
            </div>

            {/* Password */}
            <div className="input-group">
              <label className="input-label">Password</label>
              <div style={{ position: "relative" }}>
                <Lock size={16} color="var(--color-gray-400)" style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)" }} />
                <input
                  type={showPass ? "text" : "password"}
                  className="input-field"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Your password"
                  style={{ paddingLeft: "42px", paddingRight: "44px" }}
                />
                <button onClick={() => setShowPass(!showPass)}
                  style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-400)", padding: "0" }}>
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* OTP */}
            {f2a && (
              <div className="input-group" style={{ animation: "fadeInUp 0.3s ease" }}>
                <label className="input-label">One-Time Password (OTP)</label>
                <input
                  type="text"
                  className="input-field"
                  onChange={changeOTP}
                  placeholder="Enter OTP sent to your email"
                />
              </div>
            )}

            {/* Forgot password */}
            <div style={{ textAlign: "right", marginBottom: "24px", marginTop: "-8px" }}>
              <span onClick={forgotPassword} style={{ fontSize: "13px", fontWeight: 600, color: "var(--color-primary)", cursor: "pointer", transition: "opacity 0.15s" }}
                onMouseEnter={e => e.target.style.opacity = "0.75"}
                onMouseLeave={e => e.target.style.opacity = "1"}>
                Forgot password?
              </span>
            </div>

            {/* Submit */}
            <button className="btn-primary" onClick={login} disabled={loading}
              style={{ width: "100%", justifyContent: "center", padding: "14px", fontSize: "16px", opacity: loading ? 0.7 : 1 }}>
              {loading ? (
                <>
                  <span className="spinner" style={{ width: "18px", height: "18px", borderWidth: "2px" }} />
                  Signing in…
                </>
              ) : (
                <>Sign In <ArrowRight size={18} /></>
              )}
            </button>

            {/* Divider */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px", margin: "24px 0" }}>
              <div className="divider" style={{ flex: 1, margin: 0 }} />
              <span style={{ fontSize: "12px", color: "var(--color-gray-400)", fontWeight: 500 }}>New to Agrivestia?</span>
              <div className="divider" style={{ flex: 1, margin: 0 }} />
            </div>

            {/* Register link */}
            <button className="btn-ghost" onClick={() => window.location.href = "/register"}
              style={{ width: "100%", justifyContent: "center", padding: "13px", fontSize: "15px" }}>
              Create a Free Account
            </button>
          </div>

          {/* Footer note */}
          <p style={{ fontSize: "12px", color: "var(--color-gray-400)", textAlign: "center", marginTop: "24px", lineHeight: 1.6 }}>
            By signing in, you agree to Agrivestia's{" "}
            <span style={{ color: "var(--color-primary)", cursor: "pointer", textDecoration: "underline" }} onClick={() => window.location.href = "/terms-of-service"}>Terms of Service</span>
            {" "}and{" "}
            <span style={{ color: "var(--color-primary)", cursor: "pointer", textDecoration: "underline" }} onClick={() => window.location.href = "/privacy-policy"}>Privacy Policy</span>.
          </p>
        </div>

        {/* Landing link */}
        <div style={{ marginTop: "32px", textAlign: "center", display: "flex", gap: "24px", justifyContent: "center" }}>
          <span onClick={() => window.location.href = "/"} style={{ fontSize: "13px", color: "var(--color-gray-400)", cursor: "pointer", transition: "color 0.15s" }}
            onMouseEnter={e => e.target.style.color = "var(--color-primary)"}
            onMouseLeave={e => e.target.style.color = "var(--color-gray-400)"}>
            ← Back to Homepage
          </span>
          <span onClick={() => window.location.href = "/blog"} style={{ fontSize: "13px", color: "var(--color-gray-400)", cursor: "pointer", transition: "color 0.15s", borderLeft: "1px solid var(--color-gray-200)", paddingLeft: "24px" }}
            onMouseEnter={e => e.target.style.color = "var(--color-primary)"}
            onMouseLeave={e => e.target.style.color = "var(--color-gray-400)"}>
            Read our Blog →
          </span>
        </div>

        <style>{`
        `}</style>
      </div>
    </div>
  );
}
